import { ApolloCache, type Reference } from '@apollo/client/cache'
import { gql } from '@apollo/client/core'
import { addListItemToCache, deleteListItemFromCache } from '/@/utils/apollo'
import { useDesignerCache } from '/@/data/designer/utils'
import { useUser } from '/@/data/user'
import { getClientsGqlQuery } from './api'
import { clientPropsFragment } from './fragments'
import { type Client, type ClientWithInvoices } from './types'

interface Options {
  insertMode: 'prepend' | 'append'
}

export function useClientCache() {
  const designerCache = useDesignerCache()
  const user = useUser()

  function getCachedClients(cache: ApolloCache<unknown>) {
    const results = cache.readQuery<{
      designer: { clients: ClientWithInvoices[] }
    }>({
      query: getClientsGqlQuery,
      variables: {
        id: user.current?.designerId,
      },
    })
    return results?.designer.clients ?? []
  }

  function getCachedClient(cache: ApolloCache<unknown>, clientId: string) {
    return getCachedClients(cache).find(c => c.id === clientId)
  }

  function deleteClient(cache: ApolloCache<unknown>, clientId: string) {
    designerCache.deleteListItem(cache, 'clients', clientId)
  }

  function addClient(cache: ApolloCache<unknown>, client: Client) {
    const newClientRef = cache.writeFragment({
      data: client,
      fragmentName: 'NewClient',
      fragment: gql`
        ${clientPropsFragment}
        fragment NewClient on Client {
          ...ClientProps
        }
      `,
    })
    if (!newClientRef) return
    designerCache.addListItem(cache, 'clients', newClientRef, {
      insertMode: 'prepend',
    })
  }

  // Less generic to allow updating all cached clients with the deleted project
  function deleteClientProject(cache: ApolloCache<unknown>, projectId: string) {
    const clients = getCachedClients(cache)
    for (const client of clients) {
      if (client.projects?.find(p => p.id === projectId)) {
        const id = cache.identify({ ...client })
        deleteListItemFromCache<Client>(id, cache, 'projects', projectId)
      }
    }
  }

  function addClientListItem(
    cache: ApolloCache<unknown>,
    clientId: string,
    fieldName: keyof Client,
    newItemRef: Reference,
    options: Options = { insertMode: 'append' },
  ) {
    const client = getCachedClient(cache, clientId)
    if (client) {
      const id = cache.identify({ ...client })
      return addListItemToCache<ClientWithInvoices>(
        id,
        cache,
        fieldName,
        newItemRef,
        options,
      )
    }
  }

  return { deleteClient, addClient, deleteClientProject, addClientListItem }
}
